import {
  AlertIcon,
  Banner,
  Box,
  Button,
  EditIcon,
  Flex,
  Image,
  InfoIcon,
  Typography,
} from '@iheartjane/reefer';
import { useCallback, useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { Controller, useFormContext } from 'react-hook-form';

import { useFetchCurrentBloomMenu } from '@jane/business-admin/data-access';
import {
  ACCEPTED_IMAGE_FILE_TYPES,
  MAX_IMAGE_SIZE,
  VALID_IMAGE_MIME_TYPES,
} from '@jane/business-admin/util';
import { FileUploadArea } from '@jane/shared-b2b/components';
import { ImagePreview } from '@jane/shared/util';

import styles from '../../../PremiumLogo.module.css';

const imgTooLarge = (file: File) =>
  file.size <= MAX_IMAGE_SIZE ||
  'This logo is over the 2MB limit. Resize it and try again.';
const imgWrongFormat = (file: File) =>
  VALID_IMAGE_MIME_TYPES.includes(file.type) ||
  'This file type is not supported. Try using .jpg or .png files.';

export const LogoSection = () => {
  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const formMethods = useFormContext();
  const { setValue, watch } = formMethods;
  const imageUrl = watch('logo');
  const [imageErrors, setImageErrors] = useState<(string | boolean)[]>();

  const onDrop = useCallback(
    async ([file]: File[]) => {
      if (!file) {
        return;
      }
      const errorMessages = [imgTooLarge(file), imgWrongFormat(file)].filter(
        (result) => result !== true
      );

      if (errorMessages.length > 0) {
        setImageErrors(errorMessages);
      } else {
        const image = await ImagePreview.parse(file);
        setValue('logo', image.url, { shouldDirty: true });
        setImageErrors([]);
      }
    },
    [setValue]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: ACCEPTED_IMAGE_FILE_TYPES,
    onDrop,
    onDropRejected: (rejections: FileRejection[]) => {
      rejections.forEach((rejection) => {
        if (rejection.errors[0].code === 'file-invalid-type') {
          setImageErrors([
            'This file type is not supported. Try using .jpg or .png files.',
          ]);
        }
      });
    },
    noClick: true,
  });
  return (
    <Box pb={24}>
      <Typography mb={16}>
        Upload your logo that will appear in the header of your homepage
      </Typography>

      {imageErrors?.map((error, i) => (
        <Banner
          full
          mb={16}
          icon={<AlertIcon />}
          label={error}
          variant="error"
          key={i}
        />
      ))}

      <div {...getRootProps()}>
        {imageUrl ? (
          <Box
            className={styles['imageContainer']}
            borderRadius="sm"
            border="grays-light"
            mb={16}
            p={16}
            position="relative"
          >
            <Image
              key={imageUrl}
              src={imageUrl}
              altText="Menu Logo"
              height="264px"
              width="264px"
              borderRadius="16px"
              objectFit="contain"
            />
            <Box position="absolute" top={16} right={16}>
              <Button.Icon
                label="Edit"
                variant="secondary"
                icon={<EditIcon />}
                onClick={open}
              />
              {/* Hidden input for form value (image URL string) */}
              <Controller
                name="logo"
                render={({ field }) => <input type="hidden" {...field} />}
              />
              {/* File input for dropzone */}
              <input {...getInputProps()} />
            </Box>
          </Box>
        ) : (
          <>
            <FileUploadArea
              accept={ACCEPTED_IMAGE_FILE_TYPES}
              onDrop={onDrop}
              isLoading={
                bloomMenuQuery.isLoading ||
                (bloomMenuQuery.isFetching && !imageUrl)
              }
              label="Upload image"
              dragLabel="Drag and drop image here"
            />
          </>
        )}
        <Flex alignContent="center" mt={12}>
          <InfoIcon size="sm" mr={12} mt={4} color="grays-mid" />
          <Typography variant="mini" width={'90%'} color="grays-mid">
            File must be a JPG or PNG under 2MB
          </Typography>
        </Flex>
      </div>
    </Box>
  );
};
